import { c as _c } from "react/compiler-runtime";
/**
 * Copyright (c) 2020 A Beautiful Site, LLC
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import { type MouseEventHandler, useCallback, useEffect, useRef } from 'react';
export function useMouseDrag(host) {
  const $ = _c(15);
  const dragging = useRef(false);
  let t0;
  if ($[0] !== host) {
    t0 = event => {
      if (!host) {
        return;
      }
      if (!dragging.current) {
        host.style.setProperty("scroll-snap-type", "none");
        dragging.current = true;
      }
      host.scrollBy({
        left: -event.movementX,
        top: -event.movementY,
        behavior: "instant" as ScrollBehavior
      });
    };
    $[0] = host;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleDragging = t0;
  let t1;
  if ($[2] !== handleDragging || $[3] !== host) {
    t1 = () => {
      if (!host) {
        return;
      }
      document.removeEventListener("pointermove", handleDragging, {
        capture: true
      });
      const startLeft = host.scrollLeft;
      const startTop = host.scrollTop;
      host.style.removeProperty("scroll-snap-type");
      host.style.setProperty("overflow", "hidden");
      const finalLeft = host.scrollLeft;
      const finalTop = host.scrollTop;
      host.style.removeProperty("overflow");
      host.style.setProperty("scroll-snap-type", "none");
      host.scrollTo({
        left: startLeft,
        top: startTop,
        behavior: "instant" as ScrollBehavior
      });
      requestAnimationFrame(async () => {
        if (startLeft !== finalLeft || startTop !== finalTop) {
          host.scrollTo({
            left: finalLeft,
            top: finalTop,
            behavior: "smooth"
          });
          await waitForEvent(host, "scrollend");
        }
        host.style.removeProperty("scroll-snap-type");
      });
      dragging.current = false;
    };
    $[2] = handleDragging;
    $[3] = host;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleDragEnd = t1;
  let t2;
  if ($[5] !== handleDragEnd || $[6] !== handleDragging || $[7] !== host) {
    t2 = event_0 => {
      if (!host) {
        return;
      }
      const canDrag = event_0.button === 0;
      if (canDrag) {
        event_0.preventDefault();
        document.addEventListener("pointermove", handleDragging, {
          capture: true,
          passive: true
        });
        document.addEventListener("pointerup", handleDragEnd, {
          capture: true,
          once: true
        });
      }
    };
    $[5] = handleDragEnd;
    $[6] = handleDragging;
    $[7] = host;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleDragStart = t2;
  let t3;
  let t4;
  if ($[9] !== handleDragEnd || $[10] !== handleDragging) {
    t3 = () => () => {
      document.removeEventListener("pointermove", handleDragging, {
        capture: true
      });
      document.removeEventListener("pointerup", handleDragEnd, {
        capture: true
      });
    };
    t4 = [handleDragEnd, handleDragging];
    $[9] = handleDragEnd;
    $[10] = handleDragging;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== handleDragStart) {
    t5 = {
      isDraggingRef: dragging,
      scrollerProps: {
        onMouseDown: handleDragStart
      }
    };
    $[13] = handleDragStart;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export function waitForEvent(el: HTMLElement, eventName: string) {
  return new Promise<void>(resolve => {
    function done(event: Event) {
      if (event.target === el) {
        el.removeEventListener(eventName, done);
        resolve();
      }
    }
    el.addEventListener(eventName, done);
  });
}